import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Space, Spin, Popconfirm , Select } from 'antd';
import axios from 'axios';
import { LogoutOutlined, EditOutlined, CopyOutlined, UserAddOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Users() {
  const navigate = useNavigate();
  const location = useLocation();

  // read values (kept as simple strings in localStorage)
  const fullName = localStorage.getItem('fullName') || '';
  const email = localStorage.getItem('email') || '';
  const role = localStorage.getItem('role') || '';
  const token = localStorage.getItem('token');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [formEdit] = Form.useForm();
  const [formAdd] = Form.useForm();

  useEffect(() => {
    const verifyUser = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/user/check-user`,
          { email, role, token }
        );
        if (!res.data.success) {
          const errMsg = res?.data?.message || 'Please Login Again.';
          toast.error(errMsg);
          localStorage.clear();
          navigate('/login');
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Please Login Again.';
        toast.error(msg);
        localStorage.clear();
        navigate('/login');
      }
    };

    verifyUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname, token, email, role]);

  useEffect(() => {
    // fetch users once verified
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/all-users`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );

      if (res.data && res.data.success) {
        setUsers(res.data.users || []);
      } else {
        toast.error(res?.data?.message || 'Failed to fetch users');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch users';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    }).catch(() => {
      toast.error('Unable to copy');
    });
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    formEdit.setFieldsValue({ FullName: record.FullName, Role: record.Role });
    setIsEditModalVisible(true);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleEditOk = async () => {
    try {
      const values = await formEdit.validateFields();
      // call update API (assumes endpoint exists)
      setLoading(true);
      await axios.put(
        `${process.env.REACT_APP_API_URL}/user/update-role`,
        {
          email: editingUser.Email,
          fullName: values.FullName,
          role: values.Role,
        },
        {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        }
      );
      toast.success('User updated');
      setIsEditModalVisible(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Update failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingUser(null);
  };

  const handleDelete = async (record) => {
    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/user/delete`,
        { email: record.Email },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
      );
      toast.success('User deleted Successfully.');
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Delete failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // --- Add User handlers ---
  const openAddModal = () => {
    formAdd.resetFields();
    setIsAddModalVisible(true);
  };

  const handleAddOk = async () => {
    try {
      const values = await formAdd.validateFields();
      const payload = {
        FullName: values.FullName,
        Email: values.Email,
        Password: values.Password,
        Role: values.Role,
      };

      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/create`,
        payload,
        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
      );

      if (res.data && res.data.success) {
        toast.success('User created');
        setIsAddModalVisible(false);
        fetchUsers();
      } else {
        toast.error(res?.data?.message || 'Create failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Create failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCancel = () => {
    setIsAddModalVisible(false);
  };

  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'FullName',
      key: 'FullName',
       width: 90,
      render: (t) => <span className="font-bold ml-14 whitespace-nowrap">{(t)}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      width: 100,
      render: (t) => <span className="font-bold ml-14 whitespace-nowrap">{(t)}</span>,
    },
    {
      title: 'Role',
      dataIndex: 'Role',
      key: 'Role',
      width: 90,
      render: (t) => <span className="font-bold ml-14 whitespace-nowrap">{(t)}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
       width: 140,
      render: (_, record) => (
        <Space className="font-bold ml-14 whitespace-nowrap">
          <Button icon={<CopyOutlined />} onClick={() => handleCopy(record.Email)} />
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete user?" onConfirm={() => handleDelete(record)} okText="Yes" cancelText="No">
            <Button danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="!mb-6 !ml-4"
        toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
      />

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Users</h2>
        <div>
          <span className="mr-4">{fullName} ({role})</span>
          <Button icon={<UserAddOutlined />} type="primary" onClick={openAddModal} className="mr-2">
            Add User
          </Button>
          <Button type="primary" icon={<LogoutOutlined />} onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spin size="large" />
        </div>
      ) : (
        <Table dataSource={users} columns={columns} rowKey="Email" 
       
        className="font-semibold text-center border border-gray-200 rounded-lg shadow-sm gray-header-table-first"
        />
      )}

      {/* Edit Modal */}
      <Modal title="Edit User" visible={isEditModalVisible} onOk={handleEditOk} onCancel={handleEditCancel} okText="Update User">
        <Form form={formEdit} layout="vertical">
          <Form.Item name="FullName" label="Full Name" rules={[{ required: true, message: 'Full name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="Role" label="Role" rules={[{ required: true, message: 'Role is required' }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Modal */}
      <Modal title="Add User" visible={isAddModalVisible} onOk={handleAddOk} onCancel={handleAddCancel} okText="Create User">
        <Form form={formAdd} layout="vertical">
          <Form.Item name="FullName" label="Full Name" rules={[{ required: true, message: 'Full name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="Email" label="Email" rules={[{ required: true, type: 'email', message: 'Valid email is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="Password" label="Password" rules={[{ required: true, message: 'Password is required' }]}>
            <Input.Password />
          </Form.Item>
         <Form.Item
  name="Role"
  label="Role"
  rules={[{ required: true, message: "Role is required" }]}
>
  <Select
    placeholder="Select role"
    options={[
      { label: "ADMIN", value: "ADMIN" },
      { label: "USER", value: "USER" },
    ]}
  />
</Form.Item>

        </Form>
      </Modal>
    </div>
  );
}

export default Users;
